import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Wand2, Package, Layers, Type, Image as ImageIcon } from "lucide-react";
import Navbar from "@/components/Navbar";

export default function Landing() {
  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      <section className="relative overflow-hidden bg-yellow-200 border-b-2 border-stone-900 grain">
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <div>
            <div className="inline-flex items-center gap-2 bg-white border-2 border-stone-900 hard-shadow-sm rounded-full px-3 py-1 mb-6" data-testid="hero-badge">
              <Sparkles className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-widest">For Shopify sellers</span>
            </div>
            <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-stone-900">
              Turn every product into a <span className="bg-pink-300 border-2 border-stone-900 hard-shadow px-2 inline-block rotate-[-1deg]">customizable</span> canvas.
            </h1>
            <p className="mt-6 text-lg text-stone-700 max-w-xl font-body">
              Build product templates once. Embed a <span className="font-bold">&ldquo;Customise now&rdquo;</span> button on any Shopify product page. Your customers design it live &mdash; you get the order with every detail attached.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/login" className="brutal-btn bg-stone-900 text-white px-6 py-3 rounded-md flex items-center gap-2" data-testid="cta-get-started">
                Start selling custom <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#how" className="brutal-btn bg-white px-6 py-3 rounded-md" data-testid="cta-learn-more">See how it works</a>
            </div>
            <p className="mt-4 text-sm font-body text-stone-600">Default seller: <code className="bg-white border-2 border-stone-900 px-2 py-0.5 rounded">admin@customizer.app</code> / <code className="bg-white border-2 border-stone-900 px-2 py-0.5 rounded">Admin@12345</code></p>
          </div>

          <div className="relative">
            <div className="brutal-card p-4 rotate-[2deg]">
              <div className="bg-stone-100 border-2 border-stone-900 rounded-md aspect-square relative overflow-hidden">
                <img src="https://images.pexels.com/photos/12025472/pexels-photo-12025472.jpeg" alt="tshirt" className="w-full h-full object-cover"/>
                <div className="absolute inset-x-8 top-24 bottom-40 border-2 border-dashed border-stone-900 bg-yellow-100/70 flex items-center justify-center">
                  <span className="font-display text-4xl text-stone-900 rotate-[-4deg]">Your logo here</span>
                </div>
                <div className="absolute bottom-6 left-0 right-0 text-center font-heading font-black text-2xl text-stone-900">CUSTOM TEE</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 brutal-card bg-green-200 px-4 py-2 rotate-[-3deg]">
              <span className="font-bold">+ live preview</span>
            </div>
            <div className="absolute -top-6 -right-6 brutal-card bg-blue-200 px-4 py-2 rotate-[4deg]">
              <span className="font-bold">Shopify ready</span>
            </div>
          </div>
        </div>
      </section>

      <section id="how" className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-3xl mb-14">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-600 mb-2">The workflow</div>
          <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Three steps. That&apos;s the whole thing.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Layers, tint: "bg-yellow-200", title: "1. Build a template", desc: "Upload your product photo. Draw zones where text or images can be added. Set font & color rules." },
            { icon: Wand2, tint: "bg-pink-200", title: "2. Embed on Shopify", desc: "Copy the Liquid snippet or hosted script. Paste on your product page. A 'Customise now' popup opens the editor." },
            { icon: Package, tint: "bg-green-200", title: "3. Get the order", desc: "Customer designs & clicks 'Add to cart'. You get the preview PNG + all text, colors, fonts, & uploaded images." },
          ].map((s, i) => (
            <div key={i} className={`brutal-card p-6 ${s.tint}`} data-testid={`step-card-${i}`}>
              <div className="w-12 h-12 bg-white border-2 border-stone-900 rounded-md flex items-center justify-center hard-shadow-sm mb-4">
                <s.icon className="w-6 h-6" />
              </div>
              <h3 className="font-heading font-black text-2xl mb-2">{s.title}</h3>
              <p className="font-body text-stone-800">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-stone-900 text-stone-50 border-t-2 border-stone-900">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-300 mb-2">Everything included</div>
              <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">A studio your customers actually want to use.</h2>
              <p className="mt-4 text-stone-300 font-body">Google Fonts, custom uploads, curve text, color palettes, image drop, live preview. Made for buyers, not designers.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Type, label: "Text + curve + font" },
                { icon: ImageIcon, label: "Image upload (50MB)" },
                { icon: Layers, label: "Restricted zones" },
                { icon: Sparkles, label: "Live preview" },
              ].map((f, i) => (
                <div key={i} className="bg-stone-800 border-2 border-yellow-300 hard-shadow rounded-md p-4">
                  <f.icon className="w-6 h-6 text-yellow-300 mb-2" />
                  <div className="font-bold">{f.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t-2 border-stone-900 py-8 text-center font-body text-stone-600">
        Built with love · CanvasKit for Shopify sellers
      </footer>
    </div>
  );
}
